import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileImage, Loader2, CheckCircle, AlertCircle, Zap, TrendingUp, Calendar, Building2, Receipt } from 'lucide-react';
import api from '../services/api';

export default function InvoiceAnalysisPage() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [profitMargin, setProfitMargin] = useState(20);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Dosya tipi kontrolü
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('Sadece JPEG, PNG veya WEBP formatı desteklenir');
      return;
    }

    // Dosya boyutu kontrolü (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Dosya boyutu 10MB\'dan küçük olmalı');
      return;
    }

    setError('');
    setResult(null);

    // Preview göster
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const analyzeInvoice = async () => {
    if (!previewUrl) {
      setError('Lütfen bir fatura görseli seçin');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await api.post('/api/invoice/analyze', {
        image_base64: previewUrl,
        profit_margin: profitMargin / 100
      });

      if (response.data.success) {
        setResult(response.data);
      } else {
        setError(response.data.error_message || 'Fatura analiz edilemedi');
      }
    } catch (err) {
      console.error('Invoice analysis error:', err);
      setError(err.response?.data?.detail || 'Fatura analizi sırasında hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPreviewUrl(null);
    setResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Fatura Analizi</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Elektrik faturanızı yükleyin, şarj tarifenizi otomatik hesaplayalım
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sol Panel - Yükleme */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-500" />
            Fatura Yükle
          </h2>

          {/* Dosya Yükleme Alanı */}
          <div 
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              previewUrl 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {previewUrl ? (
              <div className="space-y-4">
                <img 
                  src={previewUrl} 
                  alt="Fatura önizleme" 
                  className="max-h-64 mx-auto rounded-lg shadow-md"
                />
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  Değiştirmek için tıklayın
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="w-16 h-16 mx-auto bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
                  <Upload className="h-8 w-8 text-slate-400" />
                </div>
                <div>
                  <p className="text-slate-700 dark:text-slate-300 font-medium">
                    Fatura görselini yükleyin
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    JPEG, PNG veya WEBP (max 10MB)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Kar Marjı */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Kar Marjı: %{profitMargin}
            </label>
            <input
              type="range"
              min="5"
              max="50"
              value={profitMargin}
              onChange={(e) => setProfitMargin(Number(e.target.value))}
              className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-slate-500 mt-1">
              <span>%5</span>
              <span>%50</span>
            </div>
          </div>

          {/* Hata Mesajı */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Butonlar */}
          <div className="mt-6 flex gap-3">
            <button
              onClick={analyzeInvoice}
              disabled={!previewUrl || loading}
              className="flex-1 h-11 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-medium flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Analiz ediliyor...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5" />
                  Analiz Et
                </>
              )}
            </button>
            {previewUrl && (
              <button
                onClick={resetForm}
                disabled={loading}
                className="px-4 h-11 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                Temizle
              </button>
            )}
          </div>
        </div>

        {/* Sağ Panel - Sonuçlar */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-500" />
            Analiz Sonucu
          </h2>

          {result ? (
            <div className="space-y-4">
              {/* Başarı Mesajı */}
              <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <p className="text-sm text-emerald-700 dark:text-emerald-400">Fatura başarıyla analiz edildi</p>
              </div>

              {/* Sonuç Kartları */}
              <div className="grid grid-cols-2 gap-3">
                {/* Toplam Tüketim */}
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Toplam Tüketim</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {result.total_kwh ? `${result.total_kwh.toLocaleString('tr-TR')} kWh` : '-'}
                  </p>
                </div>

                {/* Toplam Tutar */}
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Toplam Tutar</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {result.total_cost ? `${result.total_cost.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL` : '-'}
                  </p>
                </div>

                {/* Birim Fiyat */}
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Birim Fiyat</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">
                    {result.unit_price ? `${result.unit_price.toLocaleString('tr-TR', { minimumFractionDigits: 4 })} TL/kWh` : '-'}
                  </p>
                </div>

                {/* Önerilen Tarife */}
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">Önerilen Tarife (+%{Math.round(result.profit_margin * 100)})</p>
                  <p className="text-xl font-bold text-blue-700 dark:text-blue-300">
                    {result.suggested_tariff ? `${result.suggested_tariff.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL/kWh` : '-'}
                  </p>
                </div>
              </div>

              {/* Ek Bilgiler */}
              <div className="pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
                {result.provider && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500 dark:text-slate-400">Sağlayıcı:</span>
                    <span className="text-slate-700 dark:text-slate-300">{result.provider}</span>
                  </div>
                )}
                {result.billing_period && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500 dark:text-slate-400">Dönem:</span>
                    <span className="text-slate-700 dark:text-slate-300">{result.billing_period}</span>
                  </div>
                )}
                {result.invoice_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <FileImage className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500 dark:text-slate-400">Fatura Tarihi:</span>
                    <span className="text-slate-700 dark:text-slate-300">{result.invoice_date}</span>
                  </div>
                )}
              </div>

              {/* Tarife Uygula Butonu */}
              <button
                onClick={() => alert('Bu özellik yakında eklenecek: Tarifeyi otomatik olarak cihazınıza uygulayın')}
                className="w-full h-11 mt-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Zap className="h-5 w-5" />
                Bu Tarifeyi Uygula
              </button>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
                  <FileImage className="h-8 w-8 text-slate-400" />
                </div>
                <p className="text-slate-500 dark:text-slate-400">
                  Fatura yükleyip analiz ettiğinizde<br />sonuçlar burada görünecek
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
